import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  // standards: allow file-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  Building2,
  Check,
  ChevronRight,
  Cloud,
  FolderOpen,
  FolderPlus,
  HardDrive,
  KeyRound,
  Layers,
  Library,
  LoaderCircle,
  Plus,
  RotateCw,
  Search,
  TriangleAlert,
  X,
} from 'lucide-react';
import {
  CLOUD_PROVIDER_KINDS,
  CLOUD_PROVIDER_WORDS,
  cloudDeliveryLine,
  cloudEarlierOffer,
  cloudLeaveWarning,
  cloudUnconfirmedLine,
  cloudUnsentLine,
  usesApiKey,
  type CloudAccountSummary,
  type CloudDestinationSummary,
  type CloudFieldMapping,
  type CloudPlace,
  type CloudProviderKind,
  type CloudProvidersAndAccounts,
  type CloudRepositoryField,
  type CloudSignInStatus,
} from '../shared/cloud-export';
import { OperatorErrorText } from './TechnicalDetails';
import type { TelemetryUiErrorCode } from '../shared/telemetry-catalog';
import { TooltipButton } from './TooltipButton';
import './cloud-destination.css';

const api = () => {
  const desktop = window.capturePointDesktop;
  if (!desktop) throw new Error("Paige's desktop connection is not ready.");
  return desktop;
};
const message = (error: unknown) =>
  error instanceof Error
    ? error.message.replace(/^Error invoking remote method '[^']+': (Error: )?/, '')
    : String(error);
/** What telemetry sends for a failed action here (`run`'s name), instead of the provider's words. */
const actionFailureCode = (name: string): TelemetryUiErrorCode =>
  name === 'sign-in' || name === 'cancel' || name === 'sign-out'
    ? 'cloud-sign-in-failed'
    : name === 'test'
      ? 'cloud-test-failed'
      : 'cloud-destination-failed';

const PROVIDER_NOTE: Record<CloudProviderKind, string> = {
  'microsoft-365': 'Microsoft 365',
  'google-drive': 'My Drive or a shared drive',
  dropbox: 'Personal or team space',
  'nucleus-one': 'A project, with its fields',
};

function ProviderIcon({ kind }: { kind: CloudProviderKind }) {
  if (kind === 'dropbox') return <HardDrive size={19} aria-hidden="true" />;
  if (kind === 'nucleus-one') return <Layers size={19} aria-hidden="true" />;
  return <Cloud size={19} aria-hidden="true" />;
}

function PlaceIcon({ kind }: { kind: CloudPlace['kind'] }) {
  if (kind === 'site') return <Building2 size={15} aria-hidden="true" />;
  if (kind === 'library') return <Library size={15} aria-hidden="true" />;
  if (kind === 'drive') return <HardDrive size={15} aria-hidden="true" />;
  return <FolderOpen size={15} aria-hidden="true" />;
}

/**
 * A place being chosen carries the account it is on: the folder picked in a picker is that
 * picker's account's, and an edit of the saved place is the saved account's. Read from
 * whichever account was signed in last, a draft of the saved folder saved under the new account.
 */
type Draft = {
  accountId: string;
  locationId: string;
  locationPath: string;
  sendValues: boolean;
  fieldMap: CloudFieldMapping[];
};

const sameMap = (a: CloudFieldMapping[], b: CloudFieldMapping[]) =>
  JSON.stringify(a.filter((line) => line.field.trim())) ===
  JSON.stringify(b.filter((line) => line.field.trim()));
type Picker = {
  accountId: string;
  trail: CloudPlace[];
  places: CloudPlace[];
  loading: boolean;
  query: string;
  naming: string | null;
  error: string | null;
};

/**
 * The job already sends to this account. Signing in (or connecting a key) as any other
 * account, even of the same service, opens the folder picker: the saved folder belongs to
 * the saved account, and its documents keep waiting for that account until a place on the
 * new one is chosen and saved.
 */
const sameAccountSaved = (
  saved: CloudDestinationSummary['destination'],
  account: { kind: CloudProviderKind; accountId: string },
) => Boolean(saved && saved.kind === account.kind && saved.accountId === account.accountId);

/**
 * Job setup › Export › Other destinations: also send each exported document to
 * SharePoint/OneDrive, Google Drive, Dropbox or Nucleus One, named by the job's
 * own naming. One provider per job; its own Save, because signing in and
 * choosing a folder happen against the provider, not in the job's draft.
 * Nucleus One connects with the person's API key, typed into Paige's
 * protected key window (never this page), and takes the job's fields through a
 * field map (`fieldNames` are the job's fields).
 */
export function CloudDestination({
  jobId,
  locked = false,
  fieldNames = [],
}: {
  jobId: string;
  locked?: boolean;
  fieldNames?: readonly string[];
}) {
  const [catalog, setCatalog] = useState<CloudProvidersAndAccounts | null>(null);
  const [summary, setSummary] = useState<CloudDestinationSummary | null>(null);
  const [kind, setKind] = useState<CloudProviderKind | null>(null);
  const [accountId, setAccountId] = useState<string | null>(null);
  const [signIn, setSignIn] = useState<CloudSignInStatus | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [picker, setPicker] = useState<Picker | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<TelemetryUiErrorCode>('cloud-destination-failed');
  const [notice, setNotice] = useState<string | null>(null);
  // Stopping or switching a destination with documents still waiting asks first.
  const [confirmLeave, setConfirmLeave] = useState<'stop' | 'save' | null>(null);
  // A repository's fields (Nucleus One's project fields), offered as field map targets.
  const [repositoryFields, setRepositoryFields] = useState<CloudRepositoryField[]>([]);
  const mounted = useRef(true);

  const load = useCallback(async () => {
    const [nextCatalog, nextSummary] = await Promise.all([
      api().cloudExportProviders(),
      api().cloudExportDestination(jobId),
    ]);
    if (!mounted.current) return;
    setCatalog(nextCatalog);
    setSummary(nextSummary);
    if (nextSummary.destination) {
      setKind((current) => current ?? nextSummary.destination!.kind);
      setAccountId((current) => current ?? nextSummary.destination!.accountId);
    }
  }, [jobId]);

  useEffect(() => {
    mounted.current = true;
    load().catch((failure) => {
      setErrorCode('cloud-destination-load-failed');
      setError(message(failure));
    });
    // The progress and the account's state both refresh: a sign-in the provider refused shows here
    // as "sign in again" without the screen being reopened.
    const timer = window.setInterval(() => {
      load().catch(() => undefined);
    }, 10_000);
    return () => {
      mounted.current = false;
      window.clearInterval(timer);
    };
  }, [jobId, load]);

  // A sign-in in the browser: ask until it ends, then come back here with the folder picker open.
  useEffect(() => {
    if (!signIn || signIn.state !== 'waiting') return;
    const timer = window.setInterval(async () => {
      try {
        const status = await api().cloudExportSignInStatus(signIn.signInId);
        if (!mounted.current || status.state === 'waiting') return;
        setSignIn(status);
        if (status.state === 'connected' && status.account) {
          await load();
          const signedIn = status.account.accountId;
          setAccountId(signedIn);
          // A folder chosen on another account is that account's: it is not saved under this one.
          setDraft((current) => (current && current.accountId !== signedIn ? null : current));
          setNotice(`Signed in as ${status.account.name}.`);
          const current = await api().cloudExportDestination(jobId);
          if (!sameAccountSaved(current.destination, status.account))
            void openPicker(status.account.accountId, []);
        } else if (status.state === 'failed') {
          setErrorCode('cloud-sign-in-failed');
          setError(status.message ?? 'The sign-in was not finished.');
        }
      } catch (failure) {
        setErrorCode('cloud-sign-in-failed');
        setError(message(failure));
      }
    }, 1_500);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signIn?.signInId, signIn?.state]);

  // Leaving this card (another Export stop, another job) ends a sign-in still waiting in the browser: the card
  // that would take it is gone, and Paige would otherwise keep waiting for it for up to 40 minutes.
  const waitingSignIn = useRef<string | null>(null);
  waitingSignIn.current = signIn?.state === 'waiting' ? signIn.signInId : null;
  useEffect(
    () => () => {
      const pending = waitingSignIn.current;
      if (!pending) return;
      try {
        void api()
          .cloudExportCancelSignIn(pending)
          .catch(() => undefined);
      } catch {
        /* the desktop connection is gone too */
      }
    },
    [],
  );

  // A picker or question belongs to one service: choosing another (or stopping) starts it afresh.
  useEffect(() => {
    setPicker(null);
    setConfirmLeave(null);
    setRepositoryFields([]);
  }, [kind]);

  const saved = summary?.destination ?? null;
  const words = kind ? CLOUD_PROVIDER_WORDS[kind] : null;
  const accounts = useMemo(
    () => (catalog?.accounts ?? []).filter((item) => item.kind === kind),
    [catalog, kind],
  );
  // The account a place is on is the one named: an open picker's, then a draft's, then the saved
  // destination's. Another account signed in since (even of the same service) is not where the saved
  // destination's documents go: they wait until that account signs in again or a folder on the new one is saved.
  // A picker's or draft's account counts only while it is still signed in here (chunk 28, R5-B1).
  const accountOf = (id: string) =>
    summary?.account?.accountId === id && summary.account.kind === kind
      ? summary.account
      : (accounts.find((item) => item.accountId === id) ?? null);
  const stillHere = (id: string | undefined) => (id !== undefined && accountOf(id) ? id : null);
  const choosingOn =
    stillHere(picker?.accountId) ??
    stillHere(draft?.accountId) ??
    (saved && saved.kind === kind ? saved.accountId : null);
  const account: CloudAccountSummary | null =
    choosingOn !== null
      ? accountOf(choosingOn)
      : ((summary?.account &&
        summary.account.kind === kind &&
        (!accountId || summary.account.accountId === accountId)
          ? summary.account
          : null) ??
        accounts.find((item) => item.accountId === accountId) ??
        (accounts.length === 1 ? accounts[0] : null));
  const shown: Draft | null =
    draft ??
    (saved && saved.kind === kind
      ? {
          accountId: saved.accountId,
          locationId: saved.locationId,
          locationPath: saved.locationPath,
          sendValues: saved.sendValues,
          fieldMap: saved.fieldMap ?? [],
        }
      : null);
  // A draft is saved under its own account, so it is a change only while that account is the one named.
  const changed = Boolean(
    draft &&
    account &&
    account.accountId === draft.accountId &&
    (!saved ||
      saved.accountId !== draft.accountId ||
      saved.locationId !== draft.locationId ||
      saved.sendValues !== draft.sendValues ||
      !sameMap(saved.fieldMap ?? [], draft.fieldMap)),
  );
  const apiKey = usesApiKey(kind);

  // The repository's fields for the chosen place, for the field map's suggestions.
  const fieldsPlace = apiKey && account?.state === 'connected' ? (shown?.locationId ?? null) : null;
  useEffect(() => {
    if (!fieldsPlace || !account) return;
    let current = true;
    api()
      .cloudExportFields(account.accountId, fieldsPlace)
      .then((fields) => {
        if (current && mounted.current) setRepositoryFields(fields);
      })
      .catch(() => undefined);
    return () => {
      current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldsPlace, account?.accountId]);

  const run = async (name: string, action: () => Promise<void>) => {
    setBusy(name);
    setError(null);
    setNotice(null);
    setErrorCode(actionFailureCode(name));
    try {
      await action();
    } catch (failure) {
      if (mounted.current) setError(message(failure));
    } finally {
      if (mounted.current) setBusy(null);
    }
  };

  const startSignIn = (provider: CloudProviderKind) =>
    run('sign-in', async () => {
      setKind(provider);
      setSignIn(await api().cloudExportSignIn(provider));
    });

  const cancelSignIn = () =>
    signIn &&
    run('cancel', async () => {
      setSignIn(await api().cloudExportCancelSignIn(signIn.signInId));
    });

  // Nucleus One: the protected key window takes the key; a refused account's key is replaced in place.
  const connectKey = (provider: CloudProviderKind) =>
    run('sign-in', async () => {
      setKind(provider);
      const replacing =
        account && account.kind === provider && account.state === 'reconnect'
          ? account.accountId
          : undefined;
      const connected = await api().cloudExportConnectKey(provider, replacing);
      if (!connected || !mounted.current) return;
      await load();
      setAccountId(connected.accountId);
      setDraft((current) =>
        current && current.accountId !== connected.accountId ? null : current,
      );
      setNotice(`Connected to ${CLOUD_PROVIDER_WORDS[provider].short} as ${connected.name}.`);
      const current = await api().cloudExportDestination(jobId);
      if (
        !sameAccountSaved(current.destination, { kind: provider, accountId: connected.accountId })
      )
        void openPicker(connected.accountId, []);
    });

  async function openPicker(forAccount: string, trail: CloudPlace[], query = '') {
    setPicker({
      accountId: forAccount,
      trail,
      places: [],
      loading: true,
      query,
      naming: null,
      error: null,
    });
    try {
      const places = await api().cloudExportBrowse(
        forAccount,
        trail.at(-1)?.id,
        query || undefined,
      );
      if (mounted.current)
        setPicker({
          accountId: forAccount,
          trail,
          places,
          loading: false,
          query,
          naming: null,
          error: null,
        });
    } catch (failure) {
      if (mounted.current)
        setPicker({
          accountId: forAccount,
          trail,
          places: [],
          loading: false,
          query,
          naming: null,
          error: message(failure),
        });
    }
  }

  const here = picker?.trail.at(-1) ?? null;
  const pathOf = (trail: CloudPlace[]) => trail.map((place) => place.name).join(' / ');

  const useHere = () => {
    if (!here || !here.selectable) return;
    setDraft({
      accountId: picker!.accountId,
      locationId: here.id,
      locationPath: pathOf(picker!.trail),
      sendValues: shown?.sendValues ?? true,
      fieldMap: shown?.fieldMap ?? [],
    });
    setPicker(null);
  };

  const makeFolder = () =>
    here &&
    picker?.naming?.trim() &&
    run('new-folder', async () => {
      const made = await api().cloudExportNewFolder(
        picker.accountId,
        here.id,
        picker.naming!.trim(),
      );
      await openPicker(picker.accountId, [...picker.trail, made]);
    });

  const save = async () => {
    if (!account || !shown) return null;
    const fieldMap = shown.fieldMap.filter((line) => line.field.trim());
    // The place's own account (shown.accountId), which is also the account named on the card.
    const next = await api().cloudExportSaveDestination(jobId, {
      ...shown,
      fieldMap,
      enabled: true,
    });
    setSummary(next);
    setDraft(null);
    return next;
  };

  // A new folder or account leaves the documents still waiting for the saved one unsent there.
  const placeChanges = Boolean(
    saved &&
    draft &&
    (saved.locationId !== draft.locationId || saved.accountId !== draft.accountId),
  );

  // Test delivery saves a changed destination first: a new place asks the same question as Save.
  const test = () =>
    run('test', async () => {
      if (changed && placeChanges && confirmLeave !== 'save' && (await waitingNow()) > 0) {
        setConfirmLeave('save');
        return;
      }
      if (changed || !saved) {
        setConfirmLeave(null);
        await save();
      }
      const result = await api().cloudExportTest(jobId);
      if (result.fields && mounted.current) setRepositoryFields(result.fields);
      if (result.succeeded) setNotice(result.message);
      else setError(result.message);
    });

  const failed = summary?.recent.filter((item) => item.state === 'failed') ?? [];
  const waitingHere = summary?.counts.waiting ?? 0;
  const leaving =
    saved && words && waitingHere > 0 ? cloudLeaveWarning(waitingHere, words.short) : null;
  const unsentLine = cloudUnsentLine(summary?.unsent ?? 0, summary?.unsentFrom);
  const unconfirmedLine = words
    ? cloudUnconfirmedLine(summary?.counts.unconfirmed ?? 0, words.short)
    : null;
  const resendUnconfirmed = () =>
    void run('unconfirmed', async () => {
      const { queued } = await api().cloudExportResendUnconfirmed(jobId);
      await load();
      setNotice(
        queued === 1
          ? '1 document is on its way again.'
          : `${queued} documents are on their way again.`,
      );
    });
  const setMap = (fieldMap: CloudFieldMapping[]) => shown && setDraft({ ...shown, fieldMap });
  const sendUnsent = () =>
    void run('unsent', async () => {
      const { queued } = await api().cloudExportSendUnsent(jobId);
      await load();
      setNotice(
        queued === 1 ? '1 document is on its way.' : `${queued} documents are on their way.`,
      );
    });
  const earlier = cloudEarlierOffer(summary?.earlierExports ?? 0);
  // Asks the engine, not the last refresh, how many wait: a backlog just queued (or an export just
  // finished) must still bring the question up.
  const waitingNow = async () => {
    const fresh = await api().cloudExportDestination(jobId);
    if (mounted.current) setSummary(fresh);
    return fresh.counts.waiting;
  };
  const removeNow = async () => {
    await api().cloudExportRemoveDestination(jobId);
    setDraft(null);
    setPicker(null);
    await load();
    setKind(null);
    setNotice(`Stopped sending to ${words!.short}.`);
  };

  // Nothing is shown until a provider is verified on the real path (or one is already set up).
  const offered = CLOUD_PROVIDER_KINDS.filter(
    (provider) => catalog?.providers.find((item) => item.kind === provider)?.available,
  );
  if (catalog && summary && offered.length === 0 && !summary.destination) return null;

  // Still loading, or this engine cannot answer: show nothing rather than a half-working card.
  if (!catalog || !summary) return null;

  // Export's "Also send each document to" panel heads this card, so it carries no heading of its own.
  return (
    <div className="cloud-destination" aria-label="Also send to">
      {!saved && unsentLine && (
        <p className="cloud-unsent" role="status">
          <TriangleAlert size={14} aria-hidden="true" />
          {unsentLine} Choose where to send them.
        </p>
      )}
      {!kind && (
        <div
          className="export-destination-choices cloud-choices"
          role="group"
          aria-label="Cloud storage"
        >
          {offered.map((provider) => {
            return (
              <button
                key={provider}
                type="button"
                className="export-destination-choice"
                disabled={locked}
                onClick={() => setKind(provider)}
              >
                <span className="export-route-icon">
                  <ProviderIcon kind={provider} />
                </span>
                <span>
                  <b>{CLOUD_PROVIDER_WORDS[provider].title}</b>
                  <small>{PROVIDER_NOTE[provider]}</small>
                </span>
              </button>
            );
          })}
        </div>
      )}

      {kind && words && (
        <div className="cloud-card">
          <div className="cloud-card-head">
            <span className="export-route-icon">
              <ProviderIcon kind={kind} />
            </span>
            <div>
              <b>{words.title}</b>
              {account && (
                <small>
                  {account.state === 'reconnect'
                    ? `${account.name} · ${apiKey ? 'enter the API key again' : 'sign in again'} to keep sending`
                    : account.name}
                </small>
              )}
            </div>
            {!saved && signIn?.state !== 'waiting' && (
              <button
                type="button"
                className="cloud-text-button"
                disabled={locked}
                onClick={() => {
                  setKind(null);
                  setDraft(null);
                  setPicker(null);
                  setAccountId(null);
                }}
              >
                Other services
              </button>
            )}
            {account && account.state === 'connected' && (
              <button
                type="button"
                className="cloud-text-button"
                disabled={busy !== null}
                onClick={() =>
                  void run('sign-out', async () => {
                    const signedOut = account.accountId;
                    await api().cloudExportSignOut(signedOut);
                    setAccountId(null);
                    setPicker(null);
                    // A folder chosen on the account signed out of is not saved under the next one.
                    setDraft((current) =>
                      current && current.accountId === signedOut ? null : current,
                    );
                    await load();
                    setNotice(
                      apiKey
                        ? `Paige forgot its ${words.short} API key. You can also delete the key in ${words.short}.`
                        : `Signed out of ${words.short}.`,
                    );
                  })
                }
              >
                {apiKey ? 'Remove key' : 'Sign out'}
              </button>
            )}
          </div>

          {signIn?.state === 'waiting' ? (
            <div className="cloud-waiting" role="status">
              <LoaderCircle className="spin" size={15} aria-hidden="true" />
              <span>
                Finish signing in to {words.short} in your browser. Paige waits up to 40 minutes.
              </span>
              <button type="button" className="secondary-button small" onClick={cancelSignIn}>
                Cancel
              </button>
            </div>
          ) : !account || account.state === 'reconnect' ? (
            apiKey ? (
              <div className="cloud-row">
                <button
                  type="button"
                  className="primary-button"
                  disabled={locked || busy !== null}
                  onClick={() => connectKey(kind)}
                >
                  {busy === 'sign-in' ? (
                    <LoaderCircle className="spin" size={15} aria-hidden="true" />
                  ) : (
                    <KeyRound size={15} aria-hidden="true" />
                  )}
                  {account ? 'Enter the API key again' : 'Connect with an API key'}
                </button>
                <small className="cloud-muted">
                  In {words.short}: your picture › API Keys › Add API Key. You paste it into a
                  protected window.
                </small>
              </div>
            ) : (
              <div className="cloud-row">
                <button
                  type="button"
                  className="primary-button"
                  disabled={locked || busy !== null}
                  onClick={() => startSignIn(kind)}
                >
                  {busy === 'sign-in' ? (
                    <LoaderCircle className="spin" size={15} aria-hidden="true" />
                  ) : null}
                  Sign in to {words.short}
                </button>
                <small className="cloud-muted">Opens in your browser.</small>
              </div>
            )
          ) : (
            <>
              <div className="cloud-row cloud-folder">
                <FolderOpen size={16} aria-hidden="true" />
                <span className={shown ? 'cloud-path' : 'cloud-muted'}>
                  {shown?.locationPath || (shown ? 'Chosen folder' : 'No folder chosen yet')}
                </span>
                {!picker && (
                  <button
                    type="button"
                    className="secondary-button small"
                    disabled={locked || busy !== null}
                    onClick={() => void openPicker(account.accountId, [])}
                  >
                    {shown ? 'Change' : words.pick}
                  </button>
                )}
              </div>

              {picker && (
                <div className="cloud-picker" role="dialog" aria-label={words.pickTitle}>
                  <div className="cloud-picker-head">
                    <b>{words.pickTitle}</b>
                    <button
                      type="button"
                      className="cloud-text-button"
                      onClick={() => setPicker(null)}
                    >
                      Close
                    </button>
                  </div>
                  <nav className="cloud-trail" aria-label="Where you are">
                    <button
                      type="button"
                      className="cloud-text-button"
                      onClick={() => void openPicker(picker.accountId, [])}
                    >
                      {words.short}
                    </button>
                    {picker.trail.map((place, index) => (
                      <span key={place.id}>
                        <ChevronRight size={13} aria-hidden="true" />
                        <button
                          type="button"
                          className="cloud-text-button"
                          onClick={() =>
                            void openPicker(picker.accountId, picker.trail.slice(0, index + 1))
                          }
                        >
                          {place.name}
                        </button>
                      </span>
                    ))}
                  </nav>
                  {picker.trail.length === 0 && (
                    <form
                      className="cloud-search"
                      onSubmit={(event) => {
                        event.preventDefault();
                        void openPicker(picker.accountId, [], picker.query);
                      }}
                    >
                      <Search size={14} aria-hidden="true" />
                      <input
                        value={picker.query}
                        placeholder={words.search}
                        aria-label={words.search}
                        onChange={(event) => setPicker({ ...picker, query: event.target.value })}
                      />
                    </form>
                  )}
                  <ul className="cloud-places">
                    {picker.loading && (
                      <li className="cloud-muted">
                        <LoaderCircle className="spin" size={14} aria-hidden="true" /> Loading…
                      </li>
                    )}
                    {!picker.loading && picker.error && (
                      <li className="cloud-error">
                        <OperatorErrorText message={picker.error} />
                      </li>
                    )}
                    {!picker.loading && !picker.error && picker.places.length === 0 && (
                      <li className="cloud-muted">No folders here yet.</li>
                    )}
                    {picker.places.map((place) => (
                      <li key={place.id}>
                        <button
                          type="button"
                          onClick={() =>
                            void openPicker(picker.accountId, [...picker.trail, place])
                          }
                        >
                          <PlaceIcon kind={place.kind} />
                          <span>{place.name}</span>
                          <ChevronRight size={14} aria-hidden="true" />
                        </button>
                      </li>
                    ))}
                  </ul>
                  <div className="cloud-picker-foot">
                    {here?.selectable &&
                      (picker.naming === null ? (
                        <button
                          type="button"
                          className="secondary-button small"
                          disabled={busy !== null}
                          onClick={() => setPicker({ ...picker, naming: '' })}
                        >
                          <FolderPlus size={14} aria-hidden="true" />
                          New folder
                        </button>
                      ) : (
                        <form
                          className="cloud-new-folder"
                          onSubmit={(event) => {
                            event.preventDefault();
                            void makeFolder();
                          }}
                        >
                          <input
                            autoFocus
                            value={picker.naming}
                            placeholder="Folder name"
                            aria-label="New folder name"
                            maxLength={200}
                            onChange={(event) =>
                              setPicker({ ...picker, naming: event.target.value })
                            }
                          />
                          <button
                            type="submit"
                            className="secondary-button small"
                            disabled={!picker.naming.trim() || busy !== null}
                          >
                            {busy === 'new-folder' ? (
                              <LoaderCircle className="spin" size={14} aria-hidden="true" />
                            ) : null}
                            Create
                          </button>
                          <button
                            type="button"
                            className="cloud-text-button"
                            onClick={() => setPicker({ ...picker, naming: null })}
                          >
                            Cancel
                          </button>
                        </form>
                      ))}
                    <button
                      type="button"
                      className="primary-button small"
                      disabled={!here?.selectable}
                      onClick={useHere}
                    >
                      {here?.selectable ? `Use ${here.name}` : 'Open a folder to use it'}
                    </button>
                  </div>
                </div>
              )}

              {words.values && shown && (
                <label className="cloud-check">
                  <input
                    type="checkbox"
                    checked={shown.sendValues}
                    disabled={locked}
                    onChange={(event) => setDraft({ ...shown, sendValues: event.target.checked })}
                  />
                  {words.values}
                </label>
              )}

              {apiKey && shown && (
                <FieldMapEditor
                  map={shown.fieldMap}
                  onChange={setMap}
                  fields={fieldNames}
                  targets={repositoryFields.map((field) => field.name)}
                  product={words.short}
                  disabled={locked || busy !== null}
                />
              )}

              <div className="cloud-row cloud-actions">
                {changed && confirmLeave === 'save' && leaving && (
                  <span className="cloud-confirm" role="alert" data-telemetry="notice">
                    {leaving} You can send them to the new folder after saving.
                  </span>
                )}
                {changed && (
                  <button
                    type="button"
                    className="primary-button small"
                    disabled={locked || busy !== null}
                    onClick={() =>
                      void run('save', async () => {
                        if (placeChanges && confirmLeave !== 'save' && (await waitingNow()) > 0) {
                          setConfirmLeave('save');
                          return;
                        }
                        setConfirmLeave(null);
                        await save();
                        setNotice('Saved. New exports go there from now on.');
                      })
                    }
                  >
                    {busy === 'save' ? (
                      <LoaderCircle className="spin" size={14} aria-hidden="true" />
                    ) : null}
                    Save
                  </button>
                )}
                {changed && confirmLeave === 'save' && leaving && saved && (
                  <button
                    type="button"
                    className="cloud-text-button"
                    disabled={busy !== null}
                    onClick={() => {
                      setConfirmLeave(null);
                      setDraft(null);
                    }}
                  >
                    Keep sending to {saved.locationPath || 'the current folder'}
                  </button>
                )}
                {shown && (
                  <button
                    type="button"
                    className="secondary-button small"
                    disabled={locked || busy !== null}
                    onClick={() => void test()}
                  >
                    {busy === 'test' ? (
                      <LoaderCircle className="spin" size={14} aria-hidden="true" />
                    ) : null}
                    Test delivery
                  </button>
                )}
              </div>
            </>
          )}

          {saved && saved.kind === kind && (
            <div className="cloud-progress">
              <div className="cloud-row">
                <span role="status">{cloudDeliveryLine(summary.counts)}</span>
                {summary.counts.failed > 0 && (
                  <button
                    type="button"
                    className="secondary-button small"
                    disabled={busy !== null}
                    onClick={() =>
                      void run('retry', async () => {
                        await api().cloudExportRetry(jobId);
                        await load();
                      })
                    }
                  >
                    <RotateCw size={14} aria-hidden="true" />
                    Try again
                  </button>
                )}
              </div>
              {apiKey && summary.counts.sent > 0 && (
                <p className="cloud-muted">
                  {words.short} adds each document to the project a few minutes after it is sent.
                </p>
              )}
              {summary.counts.waiting > 0 && (!account || account.state === 'reconnect') && (
                <p className="cloud-waiting-reason">
                  <TriangleAlert size={14} aria-hidden="true" />
                  <span>
                    {summary.counts.waiting === 1
                      ? '1 document waits'
                      : `${summary.counts.waiting} documents wait`}{' '}
                    until you{' '}
                    {apiKey
                      ? `enter your ${words.short} API key${account ? ' again' : ''}`
                      : `sign in to ${words.short}${account ? ' again' : ''}`}
                    .
                  </span>
                </p>
              )}
              {unconfirmedLine && (
                <div
                  className="cloud-row cloud-confirm"
                  role="alert"
                  data-telemetry="error:cloud-delivery-unconfirmed"
                >
                  <span>{unconfirmedLine}</span>
                  <button
                    type="button"
                    className="secondary-button small"
                    disabled={locked || busy !== null}
                    onClick={resendUnconfirmed}
                  >
                    Not there: send again
                  </button>
                </div>
              )}
              {failed.slice(0, 3).map((item) => (
                <p key={item.exportId} className="cloud-failed">
                  <TriangleAlert size={14} aria-hidden="true" />
                  <b>{item.documentLabel}</b>
                  <span>{item.message}</span>
                </p>
              ))}
              {summary.recent
                .filter((item) => item.state === 'sent')
                .slice(0, 3)
                .map((item) => (
                  <p key={item.exportId} className="cloud-sent">
                    <Check size={14} aria-hidden="true" />
                    <span>{item.fileName}</span>
                    <small>{item.place}</small>
                  </p>
                ))}
              {earlier && (
                <div className="cloud-row cloud-earlier">
                  <span>
                    {summary.earlierExports === 1
                      ? '1 document was'
                      : `${summary.earlierExports} documents were`}{' '}
                    exported before {words.short} was added.
                  </span>
                  <button
                    type="button"
                    className="secondary-button small"
                    disabled={locked || busy !== null}
                    onClick={() =>
                      void run('earlier', async () => {
                        const { queued } = await api().cloudExportSendEarlier(jobId);
                        await load();
                        setNotice(
                          queued === 1
                            ? '1 document is on its way.'
                            : `${queued} documents are on their way.`,
                        );
                      })
                    }
                  >
                    {earlier}
                  </button>
                </div>
              )}
              {unsentLine && (
                <div className="cloud-row cloud-earlier">
                  <span>{unsentLine}</span>
                  <button
                    type="button"
                    className="secondary-button small"
                    disabled={locked || busy !== null}
                    onClick={sendUnsent}
                  >
                    Send them to {words.short}
                  </button>
                </div>
              )}
              {confirmLeave === 'stop' && leaving ? (
                <div className="cloud-row cloud-confirm" role="alert" data-telemetry="notice">
                  <span>{leaving}</span>
                  <button
                    type="button"
                    className="secondary-button small"
                    disabled={busy !== null}
                    onClick={() => {
                      setConfirmLeave(null);
                      void run('remove', removeNow);
                    }}
                  >
                    Stop anyway
                  </button>
                  <button
                    type="button"
                    className="cloud-text-button"
                    onClick={() => setConfirmLeave(null)}
                  >
                    Keep sending
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="cloud-text-button cloud-stop"
                  disabled={locked || busy !== null}
                  onClick={() =>
                    void run('remove', async () => {
                      if ((await waitingNow()) > 0) {
                        setConfirmLeave('stop');
                        return;
                      }
                      await removeNow();
                    })
                  }
                >
                  Stop sending to {words.short}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {notice && (
        <p className="cloud-notice" role="status">
          <Check size={14} aria-hidden="true" />
          {notice}
        </p>
      )}
      {error && (
        <div className="cloud-error" role="alert" data-telemetry={`error:${errorCode}`}>
          <TriangleAlert size={14} aria-hidden="true" />
          <OperatorErrorText message={error} />
        </div>
      )}
    </div>
  );
}

/**
 * A repository's field map (Nucleus One): one line per job field and the
 * repository's name for it. A field not listed goes to the field of its own
 * name; a line left empty leaves that field out ("Leave out", as Paige's map).
 */
function FieldMapEditor({
  map,
  onChange,
  fields,
  targets,
  product,
  disabled,
}: {
  map: CloudFieldMapping[];
  onChange: (map: CloudFieldMapping[]) => void;
  fields: readonly string[];
  targets: readonly string[];
  product: string;
  disabled: boolean;
}) {
  const listId = `cloud-targets-${product.replace(/\W+/g, '-').toLowerCase()}`;
  const update = (index: number, patch: Partial<CloudFieldMapping>) =>
    onChange(map.map((line, i) => (i === index ? { ...line, ...patch } : line)));
  const unused = fields.filter(
    (field) => !map.some((line) => line.field.toLowerCase() === field.toLowerCase()),
  );
  return (
    <fieldset className="cloud-field-map">
      <legend>Fields</legend>
      <small className="cloud-muted">
        Each field goes to the {product} field of the same name. Map one to another name here, or
        leave the box empty to leave it out.
      </small>
      {targets.length > 0 && (
        <datalist id={listId}>
          {targets.map((target) => (
            <option key={target} value={target} />
          ))}
        </datalist>
      )}
      {map.map((line, index) => (
        <div key={index} className="cloud-field-map-line">
          <select
            value={line.field}
            disabled={disabled}
            aria-label="Paige field"
            onChange={(event) => update(index, { field: event.target.value })}
          >
            <option value="" disabled>
              Choose a field
            </option>
            {line.field &&
              !fields.some((field) => field.toLowerCase() === line.field.toLowerCase()) && (
                <option value={line.field}>{line.field}</option>
              )}
            {fields.map((field) => (
              <option
                key={field}
                value={field}
                disabled={
                  field !== line.field &&
                  map.some((other) => other.field.toLowerCase() === field.toLowerCase())
                }
              >
                {field}
              </option>
            ))}
          </select>
          <ChevronRight size={14} aria-hidden="true" />
          <input
            value={line.target}
            disabled={disabled}
            placeholder="Leave out"
            aria-label={`${product} field`}
            list={targets.length > 0 ? listId : undefined}
            autoComplete="off"
            maxLength={128}
            onChange={(event) => update(index, { target: event.target.value })}
          />
          <TooltipButton
            type="button"
            className="cloud-text-button"
            disabled={disabled}
            aria-label="Remove line"
            tooltip="Remove"
            onClick={() => onChange(map.filter((_, i) => i !== index))}
          >
            <X size={14} aria-hidden="true" />
          </TooltipButton>
        </div>
      ))}
      {unused.length > 0 && (
        <button
          type="button"
          className="cloud-text-button cloud-map-add"
          disabled={disabled}
          onClick={() =>
            onChange([
              ...map,
              {
                field: unused[0],
                target:
                  targets.find((target) => target.toLowerCase() === unused[0].toLowerCase()) ?? '',
              },
            ])
          }
        >
          <Plus size={13} aria-hidden="true" />
          Map a field
        </button>
      )}
    </fieldset>
  );
}
